namespace NasVault {
  // D06(UI 선차단 절반) + A2(삭제 봉인 안내): 서버 403/봉인의 편의 절반은 이 사유 노드가 맡는다.
  // #nasStatus 는 load() 성공마다 setStatus("")로 비워지는 단일 슬롯이라 재사용할 수 없다 —
  // renderFiles가 매 렌더마다 #nasFiles 상단에 다시 그린다.
  const NAS_WRITE_BLOCKED_MESSAGE = "이 폴더는 쓰기가 제한돼 있어요.";
  const NAS_DELETE_SEALED_MESSAGE = "삭제는 점검 중이라 잠시 중단했어요.";

  function renderReasonNode(container: HTMLElement): void {
    const messages: string[] = [];
    if (!state.canWrite) messages.push(NAS_WRITE_BLOCKED_MESSAGE);
    if (!state.deleteEnabled) messages.push(NAS_DELETE_SEALED_MESSAGE);
    if (!messages.length) return;
    const node = document.createElement("div");
    node.className = "nas-pane-caption";
    node.textContent = messages.join(" ");
    container.append(node);
  }

  /** 미렌더(hide) 원칙 — `disabled`는 `<label class="nas-upload">`에 효과가 없어 죽은 버튼이 된다. */
  export function renderWriteAvailability(): void {
    const uploadInput = element<HTMLInputElement>("nasUpload");
    const uploadLabel = uploadInput?.closest(".nas-upload") as HTMLElement | null;
    if (uploadLabel) uploadLabel.style.display = state.canWrite ? "" : "none";
    const newFolderButton = element<HTMLButtonElement>("nasNewFolder");
    if (newFolderButton) newFolderButton.style.display = state.canWrite ? "" : "none";
  }

  export function setStatus(message: string, error = false): void {
    const node = element("nasStatus");
    if (!node) return;
    node.textContent = message;
    node.classList.toggle("error", error);
  }

  export function renderSkeleton(): void {
    const node = element("nasFiles");
    if (!node) return;
    node.innerHTML = Array.from({ length: 5 }, () => `
      <div class="nas-skeleton-row" aria-hidden="true">
        <span class="nas-skeleton nas-skeleton-name"></span>
        <span class="nas-skeleton nas-skeleton-meta"></span>
        <span class="nas-skeleton nas-skeleton-meta"></span>
      </div>`).join("");
  }

  export function renderBreadcrumbs(path: string): void {
    const node = element("nasBreadcrumb");
    if (!node) return;
    node.textContent = "";
    const root = document.createElement("button");
    root.textContent = "파일 보관함";
    root.onclick = () => void load("/");
    node.append(root);
    let built = "";
    for (const part of path.split("/").filter(Boolean)) {
      const separator = document.createElement("span");
      separator.textContent = "/";
      separator.setAttribute("aria-hidden", "true");
      node.append(separator);
      built += `/${part}`;
      const target = built;
      const button = document.createElement("button");
      button.textContent = part;
      button.onclick = () => void load(target);
      node.append(button);
    }
  }

  export function renderSidebar(): void {
    const node = element("nasFolders");
    if (!node) return;
    node.textContent = "";
    const folders = [{ name: "big_tion", path: "/" }, ...state.rootFolders.map((folder) => ({
      name: folder.name,
      path: joinPath("/", folder.name),
    }))];
    for (const folder of folders) {
      const button = document.createElement("button");
      const active = folder.path === "/"
        ? state.currentPath === "/"
        : state.currentPath === folder.path || state.currentPath.startsWith(`${folder.path}/`);
      button.className = `nas-folder${active ? " active" : ""}`;
      button.setAttribute("aria-current", active ? "page" : "false");
      button.innerHTML = `<span class="nas-folder-icon" aria-hidden="true">▰</span><span>${escapeHtml(folder.name)}</span>`;
      button.onclick = () => void load(folder.path);
      node.append(button);
    }
  }

  function extensionLabel(item: Item): string {
    if (item.type === "folder") return "폴더";
    const extension = item.name.includes(".") ? item.name.split(".").pop() : "파일";
    return (extension || "파일").toUpperCase();
  }

  function escapeHtml(value: string): string {
    const temp = document.createElement("div");
    temp.textContent = value;
    return temp.innerHTML;
  }

  export function renderFiles(items: Item[]): void {
    state.currentItems = items;
    const node = element("nasFiles");
    if (!node) return;
    node.textContent = "";
    renderReasonNode(node);
    if (!items.length) {
      const empty = document.createElement("div");
      empty.className = "nas-empty";
      const emptyHint = state.canWrite
        ? "파일을 끌어다 놓거나 업로드 버튼을 사용하세요."
        : "이 폴더에는 파일을 추가할 수 없어요.";
      empty.innerHTML = `<span aria-hidden="true">⇧</span><strong>이 폴더는 비어 있어요</strong><p>${emptyHint}</p>`;
      node.append(empty);
      return;
    }
    for (const item of items) {
      const row = document.createElement("div");
      row.className = "nas-row";
      const main = document.createElement("button");
      main.className = "nas-row-main";
      main.innerHTML = `<span class="nas-file-icon" aria-hidden="true">${item.type === "folder" ? "▰" : "▤"}</span><span>${escapeHtml(item.name)}</span>`;
      main.onclick = () => item.type === "folder"
        ? void load(joinPath(state.currentPath, item.name))
        : window.open(`/api/nas/file?path=${encodeURIComponent(joinPath(state.currentPath, item.name))}`, "_blank");
      const badge = document.createElement("span");
      badge.className = `nas-badge ${item.type}`;
      badge.textContent = extensionLabel(item);
      const size = document.createElement("span");
      size.className = "nas-row-meta";
      size.textContent = item.type === "file" ? sizeLabel(item.size) : "—";
      const modified = document.createElement("span");
      modified.className = "nas-row-meta";
      modified.textContent = item.modifiedAt ? new Date(item.modifiedAt).toLocaleString("ko-KR") : "—";
      row.append(main, badge, size, modified);
      if (state.deleteEnabled) {
        const menu = document.createElement("button");
        menu.className = "nas-more";
        menu.textContent = "⋯";
        menu.title = `${item.name} 작업`;
        menu.setAttribute("aria-label", `${item.name} 삭제 메뉴`);
        menu.onclick = () => openDeleteDialog(item);
        row.append(menu);
      }
      node.append(row);
    }
  }
}
